/**
 * The front of a procedural range: control strip, oven door(s) and the
 * horizontal door handle(s), in the range's own box-centred frame.
 *
 * ONE source for two consumers that must agree to the millimetre:
 *   - `rangeRenderer` (web-app proceduralRenderers/applianceRenderers.js)
 *     draws the door and the handle from it;
 *   - kitchen staging hangs a tea towel over that handle, planning in
 *     shared-lib with no THREE and no renderer to ask.
 * When the renderer kept these numbers to itself, the only way for the
 * planner to know where the handle was would have been a second copy of the
 * arithmetic — and the first time either changed, the towel would hang in
 * front of the oven glass.
 *
 * Frame (matches every wall-mounted procedural object): origin at the box
 * centre, +X along the wall, +Y up, +Z out of the front face. Metres.
 */

const { KITCHEN_TOE_KICK_HEIGHT } = require('./constants');

const IN = 0.0254;
/** Door fronts pulled in slightly from the body. */
const RANGE_PANEL_INSET = 0.005;
/** Handle stand-off from the door surface (~1 in). */
const RANGE_HANDLE_GAP = 0.025;
/** The control strip's share of the range's height. */
const RANGE_STRIP_RATIO = 0.12;
const RANGE_DOOR_THICKNESS = 0.015;
/** Handle centre below the door's top edge. */
const RANGE_HANDLE_DROP = 0.04;
/** A handle runs this share of its door's width. */
const RANGE_HANDLE_LENGTH_RATIO = 0.85;
/** Handle bar radius (~5/8 in diameter). */
const RANGE_HANDLE_RADIUS = 0.008;
/** At or above this width a range is a pro range: two ovens, two handles. */
const STOVE_PRO_WIDTH_THRESHOLD = 42 * IN;
/** A pro range's left oven takes this share of the door width. */
const PRO_LEFT_RATIO = 0.66;
const PRO_INTER_DOOR_GAP = 0.005;

/**
 * @param {{width:number, height:number, depth:number}} dims  metres
 * @returns {{
 *   isPro: boolean,
 *   stripH: number, doorH: number, doorW: number, doorThickness: number,
 *   doorY: number, doorZ: number, handleY: number, handleZ: number,
 *   doors: {x:number, width:number}[],
 *   handles: {x:number, y:number, z:number, length:number, radius:number}[],
 * }}
 */
function rangeFrontLayout({ width, height, depth }) {
  const isPro = width >= STOVE_PRO_WIDTH_THRESHOLD;
  const bodyHeight = height - KITCHEN_TOE_KICK_HEIGHT;
  const stripH = height * RANGE_STRIP_RATIO;
  const doorH = bodyHeight - stripH - 2 * RANGE_PANEL_INSET;
  const doorW = width - 2 * RANGE_PANEL_INSET;
  const doorThickness = RANGE_DOOR_THICKNESS;
  const doorZ = depth / 2 + doorThickness / 2;
  const doorY =
    -height / 2 + KITCHEN_TOE_KICK_HEIGHT + doorH / 2 + RANGE_PANEL_INSET;
  const handleY = doorY + doorH / 2 - RANGE_HANDLE_DROP;
  const handleZ = doorZ + RANGE_HANDLE_GAP;

  let doors;
  if (isPro) {
    const leftW =
      (doorW + PRO_INTER_DOOR_GAP) * PRO_LEFT_RATIO - PRO_INTER_DOOR_GAP;
    const rightW =
      (doorW + PRO_INTER_DOOR_GAP) * (1 - PRO_LEFT_RATIO) - PRO_INTER_DOOR_GAP;
    doors = [
      { x: -doorW / 2 + leftW / 2, width: leftW },
      { x: doorW / 2 - rightW / 2, width: rightW },
    ];
  } else {
    doors = [{ x: 0, width: doorW }];
  }
  const handles = doors.map((d) => ({
    x: d.x,
    y: handleY,
    z: handleZ,
    length: d.width * RANGE_HANDLE_LENGTH_RATIO,
    radius: RANGE_HANDLE_RADIUS,
  }));

  return {
    isPro,
    stripH,
    doorH,
    doorW,
    doorThickness,
    doorY,
    doorZ,
    handleY,
    handleZ,
    doors,
    handles,
  };
}

module.exports = {
  rangeFrontLayout,
  RANGE_PANEL_INSET,
  RANGE_HANDLE_GAP,
  RANGE_HANDLE_RADIUS,
  STOVE_PRO_WIDTH_THRESHOLD,
};
